import React from "react";
import { IconButton } from "@mui/material";
import AppDrawer from "../../../AppComponents/AppDrawer";
import { AppMaterialIcons } from "../../../AppComponents/AppMaterialIcons";
import { LogDetailDrawerWrapper } from "../../AppStore/AppDetail/AppDetailStyles";
import { ApiLog } from "../../../Store/Slices/APILibrary/apiLogs.service";

interface Props {
  open: boolean;
  onClose: () => void;
  log: ApiLog;
}

const ApiLogDetailDrawer: React.FC<Props> = ({ open, onClose, log }) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const renderData = (data: unknown) => {
    if (Array.isArray(data)) {
      return (
        <div className="array__list">
          {data.length === 0 ? (
            <div className="no__items">No items</div>
          ) : (
            data.map((item, index) => (
              <div className="array__item" key={index}>
                <pre>
                  {typeof item === "object"
                    ? JSON.stringify(item, null, 2)
                    : String(item)}
                </pre>
              </div>
            ))
          )}
        </div>
      );
    }

    return (
      <pre>
        {typeof data === "object" && data !== null
          ? JSON.stringify(data as Record<string, unknown>, null, 2)
          : String(data ?? "")}
      </pre>
    );
  };

  return (
    <AppDrawer open={open} onClose={onClose}>
      <LogDetailDrawerWrapper>
        <div className="drawer__header">
          <div className="header__left">
            <IconButton onClick={onClose}>
              <AppMaterialIcons iconName="ArrowBack" />
            </IconButton>
            <div className="header__title">API Log Details</div>
          </div>
          <IconButton onClick={onClose}>
            <AppMaterialIcons iconName="Close" />
          </IconButton>
        </div>

        <div className="drawer__body">
          {/* Action Section */}
          <div className="detail__section">
            <div className="section__title">Action</div>
            <div className="section__content">
              <span className="action__badge">{log.action || "N/A"}</span>
            </div>
          </div>

          {/* Basic Information */}
          <div className="detail__section">
            <div className="section__title">Basic Information</div>
            <div className="section__content">
              <div className="detail__item">
                <div className="item__label">Created Time</div>
                <div className="item__value">
                  {formatDate(log.created_time)}
                </div>
              </div>

              <div className="detail__item">
                <div className="item__label">API ID</div>
                <div className="item__value">{log.api_id}</div>
              </div>

              <div className="detail__item">
                <div className="item__label">URL</div>
                <div className="item__value" style={{ wordBreak: "break-all" }}>
                  {log.url}
                </div>
              </div>

              <div className="detail__item">
                <div className="item__label">Status Code</div>
                <div className="item__value">
                  <span
                    className={`status__badge ${
                      log.status_code >= 200 && log.status_code < 300
                        ? "success"
                        : "failed"
                    }`}
                    style={{
                      backgroundColor:
                        log.status_code >= 200 && log.status_code < 300
                          ? "#d4edda"
                          : "#f8d7da",
                      color:
                        log.status_code >= 200 && log.status_code < 300
                          ? "#155724"
                          : "#721c24",
                    }}
                  >
                    {log.status_code}
                  </span>
                </div>
              </div>

              <div className="detail__item">
                <div className="item__label">Request Type</div>
                <div className="item__value">
                  <span className="action__badge">{log.type || "N/A"}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Request */}
          {typeof log.request !== "undefined" && (
            <div className="detail__section">
              <div className="section__title">Request</div>
              <div className="section__content">
                {renderData(log.request as unknown)}
              </div>
            </div>
          )}

          {/* Headers */}
          {typeof log.header !== "undefined" && (
            <div className="detail__section">
              <div className="section__title">Headers</div>
              <div className="section__content">
                {renderData(log.header as unknown)}
              </div>
            </div>
          )}

          {/* Response */}
          {typeof (log as any).response !== "undefined" && (
            <div className="detail__section">
              <div className="section__title">Response</div>
              <div className="section__content">
                {renderData((log as any).response)}
              </div>
            </div>
          )}

          {/* Context */}
          {log.context && Object.keys(log.context).length > 0 && (
            <div className="detail__section">
              <div className="section__title">Context</div>
              <div className="section__content">
                <pre>{JSON.stringify(log.context, null, 2)}</pre>
              </div>
            </div>
          )}
        </div>
      </LogDetailDrawerWrapper>
    </AppDrawer>
  );
};

export default ApiLogDetailDrawer;
